import { Button, Heading, Surface, Text } from '@elladx/design-system';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { labelsApi } from '../api/client';
import { labelExtension, normalizeLabelFormatHint } from '../utils/labels';
interface LoadedLabel {
    objectUrl: string;
    contentType: string;
}
function PrintPage() {
    const [searchParams] = useSearchParams();
    const sourceUrl = searchParams.get('src');
    const labelFormat = normalizeLabelFormatHint(searchParams.get('format'));
    const [label, setLabel] = useState<LoadedLabel | null>(null);
    const [labelLoaded, setLabelLoaded] = useState(false);
    const [loading, setLoading] = useState(Boolean(sourceUrl));
    const [loadError, setLoadError] = useState('');
    const autoPrintedRef = useRef(false);
    const printTimerRef = useRef<number | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    useEffect(() => {
        const controller = new AbortController();
        let objectUrl: string | null = null;
        setLabel(null);
        setLabelLoaded(false);
        setLoadError('');
        setLoading(Boolean(sourceUrl));
        autoPrintedRef.current = false;
        if (!sourceUrl)
            return () => controller.abort();
        labelsApi.fetch(sourceUrl, controller.signal)
            .then(({ blob, contentType }) => {
            if (controller.signal.aborted)
                return;
            objectUrl = URL.createObjectURL(blob);
            setLabel({ objectUrl, contentType });
            setLoading(false);
        })
            .catch((err) => {
            if (controller.signal.aborted)
                return;
            setLoadError(err instanceof Error ? err.message : 'The label could not be loaded.');
            setLoading(false);
        });
        return () => {
            controller.abort();
            if (objectUrl)
                URL.revokeObjectURL(objectUrl);
            if (printTimerRef.current !== null)
                window.clearTimeout(printTimerRef.current);
        };
    }, [sourceUrl]);
    const isPdf = label?.contentType === 'application/pdf';
    const isRawPrinterFile = label
        ? ['text/plain', 'application/octet-stream'].includes(label.contentType)
        : false;
    // For PDFs the label renders inside an iframe, and printing the parent
    // document prints a blank page in many browsers (the embedded PDF viewer
    // does not render into the parent's print output) — so print via the iframe
    // itself, falling back to the parent window if the viewer blocks it.
    const printLabel = () => {
        if (isPdf) {
            try {
                const frameWindow = iframeRef.current?.contentWindow;
                if (frameWindow) {
                    frameWindow.focus();
                    frameWindow.print();
                    return;
                }
            }
            catch {
                // Fall through to printing the parent document.
            }
        }
        window.print();
    };
    const handleLabelLoad = () => {
        setLabelLoaded(true);
        setLoadError('');
        if (autoPrintedRef.current || isRawPrinterFile)
            return;
        autoPrintedRef.current = true;
        printTimerRef.current = window.setTimeout(() => printLabel(), 100);
    };
    if (!sourceUrl) {
        return (<main className="print-container">
        <Heading as="h1" variant="card" className="print-heading">EasyPost · Label printing</Heading>
        <Surface className="panel" style={{ textAlign: 'center', padding: '40px' }}>
          <Heading style={{ marginBottom: '16px' }} as="h2" variant="card">No Label Source</Heading>
          <Text style={{ marginBottom: '24px', color: 'var(--dx-color-muted)' }}>
            The label URL is missing. Please try creating a new shipment.
          </Text>
          <Button asChild={true} variant="primary"><Link to="/">Create Shipment</Link></Button>
        </Surface>
      </main>);
    }
    return (<main className={`print-container ${isPdf ? "print-container--pdf" : "print-container--thermal"}`}>
      <Heading as="h1" variant="card" className="print-heading">EasyPost · Label printing</Heading>
      <div className="print-actions">
        <Button onClick={printLabel} disabled={!labelLoaded || isRawPrinterFile} variant="primary" type="submit">
          Print Label
        </Button>
        {label && (<Button asChild={true} variant="secondary"><a href={label.objectUrl} download={`shipping-label.${labelExtension(label.contentType, labelFormat)}`}>
            Download
          </a></Button>)}
        <Button asChild={true} variant="ghost"><Link to="/">Back to Home</Link></Button>
      </div>

      {loading && (<Surface className="panel" role="status" style={{ textAlign: 'center', padding: '40px' }}>
          <Heading as="h2" variant="card">Loading label securely…</Heading>
          <Text>The label is being retrieved through your authenticated connection.</Text>
        </Surface>)}

      {!loading && loadError && (<Surface className="panel" role="alert" style={{ textAlign: 'center', padding: '40px' }}>
          <Heading style={{ marginBottom: '12px' }} as="h2" variant="card">Label Unavailable</Heading>
          <Text style={{ color: 'var(--dx-color-error)' }}>{loadError}</Text>
        </Surface>)}

      {label && !loadError && (<div className="label-container">
          {isRawPrinterFile ? (<Surface className="panel" style={{ maxWidth: '560px', textAlign: 'center' }}>
              <Heading style={{ marginBottom: '12px' }} as="h2" variant="card">Thermal Printer File</Heading>
              <Text>This authenticated {labelFormat || 'thermal'} label is printer-language data and cannot be rendered by the browser. Use Download with a compatible thermal printer.</Text>
            </Surface>) : isPdf ? (<iframe ref={iframeRef} src={label.objectUrl} title="Shipping Label" className="shipping-label" onLoad={handleLabelLoad} style={{
                    width: '8.5in',
                    height: '11in',
                    border: 'none'
                }}/>) : label.contentType.startsWith('image/') ? (<img src={label.objectUrl} alt="Shipping Label" className="shipping-label" onLoad={handleLabelLoad} onError={() => setLoadError('The authenticated label image could not be rendered.')} style={{
                    width: '4in',
                    height: 'auto',
                    maxHeight: '6in'
                }}/>) : (<Text role="alert">The proxy returned an unsupported label format.</Text>)}
        </div>)}


    </main>);
}
export default PrintPage;
